/**
 * Nav Context Store — replaces several ad-hoc `$rootScope` keys used by the
 * navigation chrome (sub-nav links, badges, breadcrumb-like state).
 *
 * Keys consolidated:
 *   clientCodeDetail        — selected client-code model (Admin/Clients sub-nav)
 *   clientCode              — selected client code string
 *   ActiveServicearea_Id    — current vendor service-area id (sub-nav vendor links)
 *   Return_Url_Invoice      — invoice deep-link return URL
 *   DisputesCount           — badge counter for the disputes tab
 *   NAVIGATE_TO_MESSAGE     — pending text-message id to open on next overview render
 */

import { create } from 'zustand';
import { setRootScopeProperty, getRootScopeProperty } from '@/lib/legacyContext';
import { getMountConfig } from '@/mountConfig';

export interface NavigateToMessage {
  textId?: string | number;
  imageId?: string | number;
  [key: string]: unknown;
}

interface NavContextState {
  clientCodeDetail: string | number | null;
  clientCode: string | null;
  activeServiceAreaId: string | number | null;
  returnUrlInvoice: string;
  disputesCount: string | null;
  navigateToMessage: NavigateToMessage | null;
}

interface NavContextActions {
  setClientCodeDetail: (v: string | number | null) => void;
  setClientCode: (v: string | null) => void;
  setActiveServiceAreaId: (v: string | number | null) => void;
  setReturnUrlInvoice: (v: string) => void;
  setDisputesCount: (v: string | null) => void;
  setNavigateToMessage: (v: NavigateToMessage | null) => void;
  hydrateFromLegacy: () => void;
  reset: () => void;
}

type NavContextStore = NavContextState & NavContextActions;

const INITIAL_STATE: NavContextState = {
  clientCodeDetail: null,
  clientCode: null,
  activeServiceAreaId: null,
  returnUrlInvoice: '',
  disputesCount: null,
  navigateToMessage: null,
};

function syncToLegacy(key: string, value: unknown): void {
  try {
    if (getMountConfig().mode === 'embedded') {
      setRootScopeProperty(key, value);
    }
  } catch {
    // graceful
  }
}

export const useNavContextStore = create<NavContextStore>()((set) => ({
  ...INITIAL_STATE,

  setClientCodeDetail: (v) => {
    set({ clientCodeDetail: v });
    syncToLegacy('clientCodeDetail', v);
  },
  setClientCode: (v) => {
    set({ clientCode: v });
    syncToLegacy('clientCode', v);
  },
  setActiveServiceAreaId: (v) => {
    set({ activeServiceAreaId: v });
    syncToLegacy('ActiveServicearea_Id', v);
  },
  setReturnUrlInvoice: (v) => {
    set({ returnUrlInvoice: v });
    syncToLegacy('Return_Url_Invoice', v);
  },
  setDisputesCount: (v) => {
    set({ disputesCount: v });
    syncToLegacy('DisputesCount', v);
  },
  setNavigateToMessage: (v) => {
    set({ navigateToMessage: v });
    syncToLegacy('NAVIGATE_TO_MESSAGE', v);
  },

  hydrateFromLegacy: () => {
    // Embedded-only — see propertyStore.hydrateFromLegacy for full reasoning.
    if (getMountConfig().mode !== 'embedded') return;
    set({
      clientCodeDetail: getRootScopeProperty<string | number | null>('clientCodeDetail') ?? null,
      clientCode: getRootScopeProperty<string | null>('clientCode') ?? null,
      activeServiceAreaId:
        getRootScopeProperty<string | number | null>('ActiveServicearea_Id') ?? null,
      returnUrlInvoice: getRootScopeProperty<string>('Return_Url_Invoice') ?? '',
      disputesCount: getRootScopeProperty<string>('DisputesCount') ?? null,
      navigateToMessage: getRootScopeProperty<NavigateToMessage>('NAVIGATE_TO_MESSAGE') ?? null,
    });
  },

  reset: () => {
    set({ ...INITIAL_STATE });
  },
}));

// Convenience selectors
export const useClientCodeDetail = (): string | number | null =>
  useNavContextStore((s) => s.clientCodeDetail);
export const useClientCode = (): string | null => useNavContextStore((s) => s.clientCode);
export const useActiveServiceAreaId = (): string | number | null =>
  useNavContextStore((s) => s.activeServiceAreaId);
export const useReturnUrlInvoice = (): string => useNavContextStore((s) => s.returnUrlInvoice);
export const useDisputesCount = (): string | null => useNavContextStore((s) => s.disputesCount);
export const useNavigateToMessage = (): NavigateToMessage | null =>
  useNavContextStore((s) => s.navigateToMessage);

/** Test-only helper. */
export function __resetNavContextStoreForTests(): void {
  useNavContextStore.setState({ ...INITIAL_STATE });
}
